import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertTriangle, CheckCircle, XCircle, Loader2, Send, ChevronRight, ArrowLeft, FileText, Search, Star, ShieldAlert, Ban, BookMarked } from 'lucide-react';
import { toast } from 'sonner';

import { RegistryLink } from '@/components/RegistryLink';
import EmptyState from '@/components/EmptyState';
import { OverrideReasons } from '@/components/referrals/OverrideReasons';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A', bg: '#F5F7FA' };

function DqFlags({ dq, submissionId, onFix, fixing }) {
  if (!dq || dq.clean) return null;
  return (
    <div className="mt-2 p-2.5 rounded-md" data-testid={`dq-flags-${submissionId}`}
      style={{ background: 'rgba(226,75,74,0.04)', border: '1px solid rgba(226,75,74,0.18)' }}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <p className="text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1.5" style={{ color: C.red }}>
          <ShieldAlert className="w-3 h-3" /> ARIA data quality — {dq.missing ? `${dq.missing} missing` : ''}{dq.missing && dq.inconsistent ? ' · ' : ''}{dq.inconsistent ? `${dq.inconsistent} inconsistent` : ''}
        </p>
        {onFix && (
          <button onClick={onFix} disabled={fixing} data-testid={`dq-fix-btn-${submissionId}`}
            className="ml-auto flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-sm text-white disabled:opacity-50 transition-opacity hover:opacity-90"
            style={{ background: C.red }}>
            {fixing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
            Send to broker to fix
          </button>
        )}
      </div>
      <div className="space-y-1">
        {dq.issues.map(i => {
          const color = i.severity === 'high' ? C.red : C.amber;
          const Icon = i.kind === 'inconsistent' ? Ban : AlertTriangle;
          return (
            <div key={i.code} className="flex items-start gap-2 text-xs" data-testid={`dq-issue-${submissionId}-${i.code.toLowerCase()}`}>
              <Icon className="w-3 h-3 mt-0.5 shrink-0" style={{ color }} />
              <span style={{ color: C.navy }}>
                <span className="font-bold text-[9px] uppercase mr-1.5 px-1 py-0.5 rounded-sm" style={{ background: `${color}1A`, color }}>{i.kind}</span>
                {i.detail}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function ReferralsPage() {
  const api = useApi();
  const [referrals, setReferrals] = useState([]);
  const [infoRequested, setInfoRequested] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deciding, setDeciding] = useState(null);
  const [referNotes, setReferNotes] = useState('');
  const [showRefer, setShowRefer] = useState(null);
  const [tab, setTab] = useState('queue');
  const [search, setSearch] = useState('');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const load = useCallback(() => {
    api.post('/submissions/dq-scan').catch(() => {}).then(() => Promise.all([
      api.get('/submissions?status=referred&limit=50'),
      api.get('/submissions?status=info_requested&limit=50'),
      api.get('/knowledge/exemplars/decided-referrals').catch(() => ({ data: { decided: [] } })),
    ])).then(([r, ir, d]) => {
      setReferrals(r.data); setInfoRequested(ir.data); setDecided(d.data.decided || []);
      const lines = [...new Set([...r.data, ...ir.data].map(s => s.insurance_class).filter(Boolean))];
      Promise.all(lines.map(l => api.get(`/knowledge/exemplars?line=${l}&k=2`).then(x => [l, x.data.exemplars]).catch(() => [l, []])))
        .then(pairs => setPrecedents(Object.fromEntries(pairs)));
    }).catch(err => { console.error('API error:', err?.message); }).finally(() => setLoading(false));
  }, [api]);
  const [precedents, setPrecedents] = useState({});
  const [decided, setDecided] = useState([]);
  const [promoting, setPromoting] = useState(null);

  const promoteExemplar = async (submissionId) => {
    setPromoting(submissionId);
    try {
      const { data } = await api.post(`/knowledge/exemplars/from-submission/${submissionId}`);
      if (data.already_promoted) toast.info('Already in the precedent library — nothing re-written');
      else toast.success(`Promoted to L6 exemplar — ${data.citation.line} pack v${data.citation.pack_version}`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not promote to exemplar'); }
    setPromoting(null);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const [pinning, setPinning] = useState(null);
  const pinGolden = async (submissionId) => {
    setPinning(submissionId);
    try {
      const { data } = await api.post(`/platform/evals/goldens/from-submission/${submissionId}`);
      if (data.already_pinned) toast.info(`Already pinned as golden case ${data.case_id}`);
      else toast.success(`Pinned as golden case ${data.case_id} — it now runs in every eval`);
    } catch { toast.error('Could not pin golden case'); }
    setPinning(null);
  };

  const handleDecide = async (submissionId, decision, notes = '') => {
    setDeciding(submissionId);
    let ok = true;
    try {
      await api.post(`/submissions/${submissionId}/decide?decision=${decision}&notes=${encodeURIComponent(notes)}`);
      setShowRefer(null);
      setReferNotes('');
      if (['approve', 'conditions', 'decline'].includes(decision)) {
        toast.success('Decision recorded', {
          duration: 8000,
          action: { label: 'Promote to exemplar', onClick: () => promoteExemplar(submissionId) },
        });
      }
      load();
    } catch (err) { console.error('API error:', err?.message); ok = false; }
    setDeciding(null);
    return ok;
  };

  const fixData = async (sub) => {
    const issues = sub.dq?.issues || [];
    const list = issues.map((i, n) => `${n + 1}) ${i.detail}.`).join(' ');
    const msg = `ARIA's data quality review flagged ${issues.length} issue${issues.length === 1 ? '' : 's'} on this submission that must be resolved before underwriting can proceed: ${list} Please correct or supply these details via the frAIme portal so the quote can be re-reviewed.`;
    const ok = await handleDecide(sub.submission_id, 'refer_broker', msg);
    if (ok) toast.success(`Data gaps sent to ${sub.broker_name || 'the broker'} — moved to Info Requested`);
    else toast.error('Could not send data gaps to the broker');
  };

  if (loading) return <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-28 rounded-md" />)}</div>;

  const tabs = [
    { id: 'queue', label: `Referral Queue (${referrals.length})` },
    { id: 'info', label: `Info Requested (${infoRequested.length})` },
    { id: 'decided', label: `Recently Decided (${decided.length})` },
  ];

  const currentList = tab === 'queue' ? referrals : tab === 'info' ? infoRequested : decided;
  const filtered = search ? currentList.filter(s => s.business_name?.toLowerCase().includes(search.toLowerCase()) || s.broker_name?.toLowerCase().includes(search.toLowerCase())) : currentList;
  const dqFlagged = currentList.filter(s => s.dq && !s.dq.clean).length;

  return (
    <div className="space-y-4" data-testid="referrals-page">
      {/* ARIA DQ banner */}
      {dqFlagged > 0 && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-md" data-testid="dq-banner"
          style={{ background: 'rgba(226,75,74,0.06)', border: '1px solid rgba(226,75,74,0.2)' }}>
          <ShieldAlert className="w-4 h-4 shrink-0" style={{ color: C.red }} />
          <p className="text-xs" style={{ color: C.navy }}>
            <strong>ARIA data quality:</strong> {dqFlagged} of {currentList.length} submission{currentList.length !== 1 ? 's' : ''} in this queue {dqFlagged === 1 ? 'has' : 'have'} missing or inconsistent data — flagged below before you review.
          </p>
        </div>
      )}
      {/* Tabs */}
      <div className="flex gap-0 border-b" style={{ borderColor: 'rgba(136,150,168,0.12)' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-[13px] font-medium border-b-2 transition-colors whitespace-nowrap ${tab === t.id ? 'border-[#2A6FC4] text-[#0D1B2A]' : 'border-transparent text-[#5C6B7A] hover:text-[#0D1B2A]'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: C.slate }} />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by business or broker..."
          className="w-full pl-10 pr-4 py-2 text-sm rounded-md border outline-none focus:border-[#2A6FC4]" style={{ borderColor: 'rgba(136,150,168,0.2)' }} />
      </div>

      {/* Recently decided — one-click promote to exemplar */}
      {tab === 'decided' ? (
        filtered.length === 0 ? (
          <Card className="p-8 rounded-md text-center" style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
            <BookMarked className="w-8 h-8 mx-auto mb-3" style={{ color: C.slate }} />
            <p className="text-sm font-medium" style={{ color: C.navy }}>No human-decided referrals yet</p>
            <p className="text-xs mt-1" style={{ color: C.slate }}>Decide a referral in the queue — it lands here for one-click promotion into the precedent library.</p>
          </Card>
        ) : filtered.map(sub => {
          const dc = sub.decision === 'APPROVE' ? C.green : sub.decision === 'DECLINE' ? C.red : C.teal;
          return (
            <Card key={sub.submission_id} data-testid={`decided-referral-${sub.submission_id}`}
              className="rounded-md bg-white" style={{ border: '1px solid rgba(136,150,168,0.12)', borderLeft: `3px solid ${dc}` }}>
              <div className="p-4 flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-sm" style={{ color: C.navy }}>{sub.business_name}</h3>
                    <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm" style={{ background: `${dc}1A`, color: dc }}>{sub.decision}</span>
                    {sub.channel === 'affinity' && (
                      <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm" style={{ background: 'rgba(108,75,181,0.1)', color: '#6C4BB5' }}>
                        Affinity · {sub.affinity_partner?.name}
                      </span>
                    )}
                    {sub.promoted && (
                      <span data-testid={`promoted-chip-${sub.submission_id}`} className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm flex items-center gap-1"
                        style={{ background: 'rgba(14,124,123,0.1)', color: '#0E7C7B' }}>
                        <BookMarked className="w-3 h-3" /> In precedent library (L6)
                      </span>
                    )}
                  </div>
                  <p className="text-xs mt-1" style={{ color: C.slate }}>
                    {sub.insurance_class || 'unknown line'} | Broker: {sub.broker_name || '—'} | Score: <span className="font-mono font-semibold" style={{ color: C.navy }}>{sub.composite_score}</span> | Decided {new Date(sub.updated_at).toLocaleDateString('en-AU')}
                  </p>
                  {sub.manual_decision_notes && <p className="text-xs mt-1 italic" style={{ color: C.slate }}>"{sub.manual_decision_notes}"</p>}
                </div>
                {!sub.promoted && (
                  <Button size="sm" onClick={() => promoteExemplar(sub.submission_id)} disabled={promoting === sub.submission_id}
                    data-testid={`promote-exemplar-${sub.submission_id}`}
                    className="rounded-md text-xs shrink-0" style={{ background: '#0E7C7B' }}>
                    {promoting === sub.submission_id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <BookMarked className="w-3 h-3 mr-1" />} Promote to exemplar
                  </Button>
                )}
              </div>
            </Card>
          );
        })
      ) : filtered.length === 0 ? (
        tab === 'queue' ? <EmptyState type="referrals" /> : (
          <Card className="p-8 rounded-md text-center" style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
            <CheckCircle className="w-8 h-8 mx-auto mb-3" style={{ color: C.green }} />
            <p className="text-sm font-medium" style={{ color: C.navy }}>No submissions awaiting information</p>
            <p className="text-xs mt-1" style={{ color: C.slate }}>All clear.</p>
          </Card>
        )
      ) : filtered.map(sub => {
        const sr = sub.scoring_result || {};
        return (
          <Card key={sub.submission_id} data-testid={`referral-${sub.submission_id}`}
            className="rounded-md bg-white overflow-hidden" style={{ border: '1px solid rgba(136,150,168,0.12)', borderLeft: `3px solid ${tab === 'queue' ? C.amber : C.teal}` }}>
            <div className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-sm" style={{ color: C.navy }}>{sub.business_name}</h3>
                    {sub.channel === 'affinity' && (
                      <span data-testid={`channel-chip-${sub.submission_id}`} className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm"
                        style={{ background: 'rgba(108,75,181,0.1)', color: '#6C4BB5' }}>
                        Affinity · {sub.affinity_partner?.name}
                      </span>
                    )}
                  </div>
                  <p className="text-xs mt-1" style={{ color: C.slate }}>
                    {sub.abn ? <RegistryLink abn={sub.abn} prefix /> : 'ABN: —'} | Broker: {sub.broker_name || '—'} | {new Date(sub.created_at).toLocaleDateString('en-AU')}
                  </p>
                </div>
                <div className="text-right">
                  <span className="font-mono text-lg font-bold" style={{ color: C.amber }}>{sub.composite_score}</span>
                  <span className="text-xs" style={{ color: C.slate }}>/10K</span>
                  {sr.confidence != null && Math.abs(sr.confidence - 0.85) > 0.001 && (
                    <p className="text-[10px] mt-0.5" style={{ color: C.amber }}>Confidence: {((sr.confidence || 0) * 100).toFixed(0)}%</p>
                  )}
                </div>
              </div>

              {/* Override reasons — with the measured citation and an audited per-reason clear */}
              <OverrideReasons sub={sub} api={api} onChanged={load} canClear={tab === 'queue'} />

              {/* ARIA DQ flags */}
              <DqFlags dq={sub.dq} submissionId={sub.submission_id}
                onFix={tab === 'queue' ? () => fixData(sub) : null}
                fixing={deciding === sub.submission_id} />

              {/* Verifier discrepancy list (cross-family extraction check) */}
              {sub.interpretation?.verification?.pass === false && (
                <div className="mt-2 p-2.5 rounded-md text-xs" style={{ background: 'rgba(226,75,74,0.06)', border: '1px solid rgba(226,75,74,0.25)', color: '#B0413E' }} data-testid={`verifier-flags-${sub.submission_id}`}>
                  <span className="font-bold">Verifier flagged this extraction: </span>
                  {(sub.interpretation.verification.discrepancies || []).join(', ')} — confirm against the source email before deciding.
                </div>
              )}

              {/* Worked exemplars — precedent beside the decision */}
              {(precedents[sub.insurance_class] || []).length > 0 && (
                <div className="mt-2 p-2.5 rounded-md" style={{ background: 'rgba(14,124,123,0.05)', border: '1px solid rgba(14,124,123,0.2)' }} data-testid={`precedents-${sub.submission_id}`}>
                  <div className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: '#0E7C7B' }}>Precedent — worked exemplars ({sub.insurance_class})</div>
                  {precedents[sub.insurance_class].map(e => (
                    <div key={e.entry_id} className="text-[11px] mb-1 last:mb-0" style={{ color: '#0D1B2A' }}>
                      {e.text}
                      <span className="ml-1.5 text-[10px]" style={{ color: '#5C6B7A' }}>
                        [{e.citation.line}{e.citation.segment ? ` · ${e.citation.segment}` : ''} pack v{e.citation.pack_version} · {e.citation.source}]
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Info requested message */}
              {sub.status === 'info_requested' && sub.info_requested && (
                <div className="mt-2 p-2.5 rounded-md" style={{ background: 'rgba(0,180,216,0.05)', border: '1px solid rgba(0,180,216,0.15)' }}>
                  <p className="text-[10px] uppercase tracking-wider font-semibold mb-1" style={{ color: C.teal }}>Information Requested</p>
                  <p className="text-xs" style={{ color: C.navy }}>{typeof sub.info_requested === 'string' ? sub.info_requested : (sub.info_requested.fields || []).join(', ')}</p>
                  <p className="text-[10px] mt-1" style={{ color: C.slate }}>Requested by {sub.info_requested_by || 'ARIA-AUTONOMY'} — {sub.info_requested_at ? new Date(sub.info_requested_at).toLocaleString() : ''}</p>
                </div>
              )}

              {/* Quick stats */}
              <div className="mt-3 grid grid-cols-4 gap-4 text-xs" style={{ color: C.slate }}>
                <span>SI: ${(sub.submission_data?.sum_insured || 0).toLocaleString()}</span>
                <span>Turnover: ${(sub.submission_data?.annual_turnover || 0).toLocaleString()}</span>
                <span>Employees: {sub.submission_data?.employee_count || 0}</span>
                <span>Claims: {sub.submission_data?.claims_count || 0}</span>
              </div>

              {/* AI Narrative */}
              {sr.ai_narrative && (
                <details className="mt-2">
                  <summary className="text-xs cursor-pointer font-medium" style={{ color: C.blue }}>View AI Narrative</summary>
                  <p className="text-xs mt-1 p-2 rounded" style={{ background: C.bg, color: C.navy }}>{sr.ai_narrative}</p>
                </details>
              )}

              {/* Actions */}
              <div className="mt-3 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => pinGolden(sub.submission_id)} disabled={pinning === sub.submission_id}
                  data-testid={`pin-golden-${sub.submission_id}`}
                  className="rounded-md text-xs" style={{ borderColor: C.blue, color: C.blue }}>
                  {pinning === sub.submission_id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Star className="w-3 h-3 mr-1" />} Pin as Golden
                </Button>
                {tab === 'queue' && (
                  <>
                    <Button size="sm" onClick={() => handleDecide(sub.submission_id, 'approve')} disabled={deciding === sub.submission_id}
                      className="rounded-md text-xs" style={{ background: C.green }}>
                      <CheckCircle className="w-3 h-3 mr-1" /> Approve & Bind
                    </Button>
                    <Button size="sm" onClick={() => handleDecide(sub.submission_id, 'conditions')} disabled={deciding === sub.submission_id}
                      className="rounded-md text-xs" style={{ background: C.teal }}>
                      Approve with Conditions
                    </Button>
                    <Button size="sm" onClick={() => setShowRefer(showRefer === sub.submission_id ? null : sub.submission_id)}
                      className="rounded-md text-xs" style={{ background: C.amber }}>
                      <Send className="w-3 h-3 mr-1" /> Refer to Broker
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleDecide(sub.submission_id, 'decline')} disabled={deciding === sub.submission_id}
                      className="rounded-md text-xs" style={{ borderColor: C.red, color: C.red }}>
                      <XCircle className="w-3 h-3 mr-1" /> Decline
                    </Button>
                  </>
                )}
                {tab === 'info' && (
                  <>
                    <Button size="sm" onClick={() => handleDecide(sub.submission_id, 'approve')} disabled={deciding === sub.submission_id}
                      className="rounded-md text-xs" style={{ background: C.green }}>
                      <CheckCircle className="w-3 h-3 mr-1" /> Approve (Info Received)
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleDecide(sub.submission_id, 'decline')} disabled={deciding === sub.submission_id}
                      className="rounded-md text-xs" style={{ borderColor: C.red, color: C.red }}>
                      Decline
                    </Button>
                  </>
                )}
              </div>

              {/* Refer to broker form */}
              {showRefer === sub.submission_id && (
                <div className="mt-3 p-3 rounded-md" style={{ background: C.bg }}>
                  <p className="text-xs font-medium mb-2" style={{ color: C.navy }}>What information do you need from the broker?</p>
                  <textarea value={referNotes} onChange={e => setReferNotes(e.target.value)}
                    placeholder="E.g. Please provide 3 years of claims history, current sum insured breakdown by location, and WHS documentation..."
                    className="w-full px-3 py-2 border rounded-md text-sm h-20 resize-none" />
                  <div className="flex gap-2 mt-2">
                    <Button size="sm" onClick={() => handleDecide(sub.submission_id, 'refer_broker', referNotes)} disabled={!referNotes.trim() || deciding === sub.submission_id}
                      className="rounded-md text-xs" style={{ background: C.amber }}>
                      {deciding === sub.submission_id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />} Send to Broker
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setShowRefer(null); setReferNotes(''); }} className="rounded-md text-xs">Cancel</Button>
                  </div>
                </div>
              )}
            </div>
          </Card>
        );
      })}
    </div>
  );
}
